/* eslint-disable max-len */
/*
 * Marker glyphs for the PM-177 stopover behaviour labels.
 *
 * Deliberately a SEPARATE module from stopTypeIcons.js, and deliberately import-free, for two
 * reasons:
 *
 *  1. stopTypeIcons.js is frozen by scripts/verify-stop-lookup-fallbacks.mjs (PORTAL-5880). That
 *     script asserts the module's export member set and both glyph-table literals are byte-identical
 *     to origin/development, and it evaluates the module with `new Function`, which cannot resolve
 *     an `import`. Adding either a new export or an import there would break the golden check that
 *     protects the untagged path.
 *  2. Behaviour labels are an axis orthogonal to load/unload. Keeping them apart stops the two from
 *     being conflated into a single "type" again.
 *
 * The geometry mirrors stopTypeIcons.js exactly - same viewBox, same disc, same index badge - so the
 * hardcoded marker anchor {x: 15, y: 18} in markerCreator.js stays correct for these glyphs too.
 */

// A verbatim copy of stopTypeIcons.js `indexTag`. It cannot be imported (see 1. above) and it must
// not be exported from there either. Third copy in the repo; src/components/Styled/Icons/StopIconTag.jsx
// is the React twin. All three branch at index > 9 and must stay in sync.
const indexTag = (index) => (index > 9
  ? '<g id="g9" transform="translate(-1,-6)">'
  + '<path id="circle4-3" style="fill:#4d4d4d;stroke:#ffffff;stroke-width:0.6632;stroke-miterlimit:2.126" d="m 23.2,20.1 c 3.5,0 6.3,2.8 6.3,6.3 0,3.5 -2.8,6.3 -6.3,6.3 0,0 -5.1,0 -5.1,0 -3.4,-0.1 -6.2,-2.9 -6.2,-6.3 0,-3.4 2.7,-6.1 6,-6.3 0.1,0 5.2,0 5.3,0 z" />'
  + `<text transform="translate(15.4056,29.735)" style="font-size:9.567px;font-family:Helvetica;fill:#ffffff" id="text7">${index}</text>`
  + '</g>'
  : '<g style="fill:none" id="g855" transform="matrix(0.53150159,0,0,0.53150159,10.045604,11.965535)">'
  + '<circle cx="14.927561" cy="15.220095" r="11.854009" id="circle4-3" style="fill:#4d4d4d;stroke:#ffffff;stroke-width:1.24779" />'
  + '<text style="font-style:normal;font-weight:normal;font-size:20.0689px;line-height:1.25;font-family:sans-serif;fill:#000000;fill-opacity:1;stroke:none" x="8.366951" y="22.535248" id="text843">'
  + `    <tspan id="tspan841" x="9.9512281" y="21.511269" style="font-size:18px;fill:#ffffff;stroke:none;stroke-opacity:1">${index}</tspan>`
  + '</text>'
  + '</g>');

/*
 * Compositeness is a thin ring inside the disc, NOT a second badge: indexTag already owns the
 * bottom-right quadrant and widens further for index > 9. Radius 7.4 clears every glyph below
 * (the widest reaches 5.6 from centre) and sits inside the 9.5 disc outline.
 *
 * The ring is a full circle, so it does pass under the sequence badge. That is fine only because
 * indexTag is concatenated after it and paints over the overlap - keep that ordering.
 */
const compositeRing = (cx, cy) => `<circle cx="${cx}" cy="${cy}" r="7.4" stroke="#ffffff" stroke-width="0.9" stroke-opacity="0.85" fill="none" id="compositeRing" />`;

// Glyph bodies, drawn relative to the disc centre so the 30x30 and indexed viewBoxes can share them.
const handoverGlyph = (cx, cy) => `<path d="M ${cx - 4.5},${cy - 2} H ${cx + 3.5} M ${cx + 1.5},${cy - 4} L ${cx + 3.5},${cy - 2} L ${cx + 1.5},${cy}" stroke="#ffffff" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" fill="none" />`
  + `<path d="M ${cx + 4.5},${cy + 2.5} H ${cx - 3.5} M ${cx - 1.5},${cy + 0.5} L ${cx - 3.5},${cy + 2.5} L ${cx - 1.5},${cy + 4.5}" stroke="#ffffff" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" fill="none" />`;

const waypointGlyph = (cx, cy) => `<path d="M ${cx},${cy - 4.5} L ${cx + 4.5},${cy} L ${cx},${cy + 4.5} L ${cx - 4.5},${cy} Z" stroke="#ffffff" stroke-width="1.2" stroke-linejoin="round" fill="none" />`;

const serviceGlyph = (cx, cy) => `<circle cx="${cx}" cy="${cy}" r="3.4" stroke="#ffffff" stroke-width="1.2" fill="none" />`
  + `<path d="M ${cx},${cy - 5.6} V ${cy - 3.9} M ${cx},${cy + 3.9} V ${cy + 5.6} M ${cx - 5.6},${cy} H ${cx - 3.9} M ${cx + 3.9},${cy} H ${cx + 5.6}" stroke="#ffffff" stroke-width="1.2" stroke-linecap="round" fill="none" />`;

// Neutral mark. The marker path never asks for it - a stop whose dominant label is OPERATIONAL
// keeps its existing load/unload glyph, which is what preserves the untagged rendering - so this
// exists as the lookup fallback only.
const operationalGlyph = (cx, cy) => `<circle cx="${cx}" cy="${cy}" r="2.6" fill="#ffffff" />`;

// Flat variants for the info bubble: same shape as stopTypeIcons' flat glyphs, so they honour the
// caller's __SIZE__ and __FILL_COLOR__ (21px in eventInfoSections.js).
const flat = (glyph) => (composite) => '<svg width="__SIZE__" height="__SIZE__" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">'
  + '  <circle cx="15" cy="14" r="10" fill="__FILL_COLOR__" />'
  + '  <circle cx="15" cy="14" r="9.5" stroke="#d47119" />'
  + `  ${glyph(15, 14)}`
  + `  ${composite ? compositeRing(15, 14) : ''}`
  + '</svg>';

// Indexed variants for the map marker. width/height mirror stopTypeIcons' indexed glyphs exactly
// (30 x 28.127065 against a 28.8316 x 27.031601 viewBox); __SIZE__ carries the width so a caller
// asking for the usual 30 reproduces that geometry and the hardcoded anchor stays valid.
const indexed = (glyph) => (index, composite) => '<svg xmlns="http://www.w3.org/2000/svg" width="__SIZE__" height="28.127065" viewBox="0 0 28.8316 27.031601" fill="none" version="1.1" id="svg10">'
  + '<circle cx="10" cy="10" r="10" fill="__FILL_COLOR__" id="circle2" />'
  + '<circle cx="10" cy="10" r="9.5" stroke="#d47119" id="circle4" />'
  + `${glyph(10, 10)}`
  + `${composite ? compositeRing(10, 10) : ''}`
  + `${indexTag(index)}`
  + '</svg>';

const behaviourToIcon = {
  HANDOVER: flat(handoverGlyph),
  WAYPOINT: flat(waypointGlyph),
  SERVICE: flat(serviceGlyph),
};

const behaviourToIndexedIcon = {
  HANDOVER: indexed(handoverGlyph),
  WAYPOINT: indexed(waypointGlyph),
  SERVICE: indexed(serviceGlyph),
};

// Same own-property guard as stopTypeIcons.js, for the same reason: callers invoke the result
// immediately, so a miss on an unrecognised label - or on an inherited member such as 'toString' -
// must degrade to a glyph rather than throw and take the map down.
const lookup = (table, tag, fallback) => (
  Object.prototype.hasOwnProperty.call(table, tag) ? table[tag] : fallback
);

export default {
  behaviourToIcon: (tag) => lookup(behaviourToIcon, tag, flat(operationalGlyph)),
  behaviourToIndexedIcon: (tag) => lookup(behaviourToIndexedIcon, tag, indexed(operationalGlyph)),
};
